import type React from 'react';
import { useEffect, useRef } from 'react';
import { Lexi } from '../../brand/lexi';
import { ArrowRightIcon } from '../../controls/icons';
import { type Countdown, type CountdownInstant, formatCountdown, useCountdown } from '../../hooks';
import { useWordsHappenTheme } from '../../theme';
import styles from './daily-countdown-card.module.scss';

export interface DailyCountdownCardProps {
  /** When today's daily ends. Leave undefined while the schedule loads. */
  endsAt?: CountdownInstant;
  /** The server's time when `endsAt` was issued, to correct for a wrong device clock. */
  serverNow?: CountdownInstant;
  onPress: () => void;
  /** Called once when the countdown reaches zero, e.g. to fetch the next daily. */
  onExpire?: () => void;
  eyebrow?: string;
  className?: string;
}

const PLACEHOLDER = '--h --m --s';

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** Remaining time for a screen reader, to the minute so the name doesn't change every second. */
export const describeCountdown = ({ h, m, expired }: Countdown): string => {
  if (expired) return '';
  if (h === 0 && m === 0) return 'under a minute';
  return [h > 0 ? plural(h, 'hour') : null, m > 0 ? plural(m, 'minute') : null].filter(Boolean).join(' ');
};

/**
 * A single tappable card: Lexi, "Today's daily ends in", a ticking countdown and an arrow. Its accessible
 * name carries the time left; the ticking digits are aria-live="off" so they aren't announced every second.
 */
export const DailyCountdownCard: React.FC<DailyCountdownCardProps> = ({
  endsAt,
  serverNow,
  onPress,
  onExpire,
  eyebrow = "Today's daily ends in",
  className,
}) => {
  const { theme } = useWordsHappenTheme();
  const countdown = useCountdown(endsAt, serverNow);
  const loaded = endsAt !== undefined;

  // Latest callback in a ref, so a new function identity each render doesn't re-fire the expiry.
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const endsAtMs = endsAt instanceof Date ? endsAt.getTime() : endsAt;
  useEffect(() => {
    if (endsAtMs !== undefined && countdown.expired) onExpireRef.current?.();
  }, [endsAtMs, countdown.expired]);

  const remaining = loaded ? describeCountdown(countdown) : '';
  const name = remaining ? `${eyebrow} ${remaining}` : eyebrow.replace(/ ends in$/i, '');

  const cls = [styles.card, theme === 'meadow' ? styles.meadow : styles.flat, className].filter(Boolean).join(' ');

  return (
    <button type="button" className={cls} aria-label={name} onClick={onPress}>
      <span aria-hidden="true" className={styles.lexi}>
        <Lexi size={52} mood="excited" />
      </span>
      <span className={styles.text}>
        <span className={styles.eyebrow}>{eyebrow}</span>
        <span className={styles.digits} aria-live="off">
          {loaded ? formatCountdown(countdown) : PLACEHOLDER}
        </span>
      </span>
      <ArrowRightIcon className={styles.arrow} size={20} strokeWidth={2} />
    </button>
  );
};
